import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputLabel,
  Paper,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';

const getGroupLabel = (row) => row?.request?.xptPgnDesc || row?.request?.npaProductSum || '-';
const getBrandLabel = (row) => {
  if (!Array.isArray(row?.brand) || row.brand.length === 0) return 'UNMAPPED';
  if (row.brand.length === 1) return row.brand[0]?.brandName || '-';
  const matchedBrand = row.brand.find((item) => item?.isMatched);
  return matchedBrand?.brandName || 'MULTIPLE';
};

const filter = createFilterOptions();

const DrugBrandMap = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { execute } = useAPICall();
  const [sessionData, setSessionData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    const init = async () => {
      const key = searchParams.get('key');
      if (!key) {
        setError('Missing brand mapping context.');
        setLoading(false);
        return;
      }

      const stored = sessionStorage.getItem(key);
      if (!stored) {
        setError('Brand mapping context is not available.');
        setLoading(false);
        return;
      }

      try {
        const parsed = JSON.parse(stored);
        setSessionData(parsed);
        const response = await execute(API_CONFIG.drug.brandMap, null, {
          showErrorToast: false,
          errorMessage: 'Unable to load brand list.',
        });
        const allBrands = Array.isArray(response) ? response : response?.data || [];
        const recommended = [];
        (parsed.selected || []).forEach((row) => {
          if (Array.isArray(row.brand) && row.brand.length > 0) {
            row.brand.forEach((brand) => {
              if (brand?.brandName && !recommended.some((item) => item.brandName === brand.brandName)) {
                recommended.push({ brandId: brand.brandId ?? null, brandName: brand.brandName });
              }
            });
          } else {
            const fallbackName = getGroupLabel(row);
            if (fallbackName !== '-' && !recommended.some((item) => item.brandName === fallbackName)) {
              recommended.push({ brandId: null, brandName: fallbackName });
            }
          }
        });
        const merged = [...recommended];
        allBrands.forEach((brand) => {
          if (brand?.brandName && !merged.some((item) => item.brandName === brand.brandName)) {
            merged.push(brand);
          }
        });
        setBrands(merged);
      } catch {
        setError('Unable to initialize brand mapping.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [execute, searchParams]);

  const groupHeader = useMemo(() => (
    sessionData?.selected?.[0]?.request?.source === 'NPA' ? 'Product Sum' : 'Group'
  ), [sessionData]);

  const saveBrand = async () => {
    if (!selectedBrand?.brandName) {
      setError('Please select a brand to map.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const next = structuredClone(sessionData);
      next.dcr[0].batchRequests = next.dcr[0].batchRequests.map((row) => {
        const existingBrands = Array.isArray(row.brand) ? row.brand : [];
        const mappedBrand = {
          brandId: selectedBrand.brandId ?? null,
          brandName: selectedBrand.brandName,
          isMatched: true,
          recordId: selectedBrand.recordId ?? null,
        };

        if (existingBrands.length === 0) {
          return { ...row, brand: [mappedBrand] };
        }

        let found = false;
        const updatedBrands = existingBrands.map((brand) => {
          const sameBrand = brand.brandName === mappedBrand.brandName && brand.brandId === mappedBrand.brandId;
          if (sameBrand) found = true;
          return { ...brand, isMatched: sameBrand };
        });
        if (!found) updatedBrands.push(mappedBrand);
        return { ...row, brand: updatedBrands };
      });

      await execute(API_CONFIG.drugDcr.updateDcr, next.dcr, {
        successMessage: 'Brand mapped successfully.',
        showSuccessToast: true,
        errorMessage: 'Unable to save brand mapping.',
      });
      navigate(`/approve-ndc?batch-id=${next?.dcr?.[0]?.batchId}&status=un-matched`);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  if (error && !sessionData && !loading) {
    return <Alert severity="error">{error}</Alert>;
  }

  const pageHeaderSx = {
    px: 2,
    py: 1.5,
    borderBottom: '1px solid #E5E7EB',
    borderLeft: '4px solid #1E88E5',
    display: 'flex',
    background: "#fbfdfe",
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    mb: 3,
  };

  return (
    <Box sx={{ maxWidth: '100vw', pb: 6, mt: 1, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PageBreadcrumbs items={[
          { label: 'Drug Inbox', path: '/inbox/drug' },
          { label: 'Approve NDC', path: `/approve-ndc?batch-id=${sessionData?.dcr?.[0]?.batchId}&status=un-matched` },
          { label: 'Brand Mapping' }
        ]} />
      </Box>

      <Paper elevation={0} sx={{
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}>
        <Box sx={pageHeaderSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.25, lineHeight: 1.2 }}>
                Drug Brand Mapping
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                Map the selected NDCs to a brand, matching the legacy drug inbox flow.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{  }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex',px:2.5, gap: 2, pb: 2,borderBottom:"1px solid #eaeaea", alignItems: 'center' }}>
                <Box sx={{ flex: 1, }}>
                   <InputLabel
                      shrink
                      sx={{
                        position: "static",
                        transform: "none",
                        mb: 0.75,
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#374151",
                      }}
                    >
                    Select Brand
                    </InputLabel>
                  <Autocomplete
                    options={brands}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') {
                        return option;
                      }
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      return option.brandName || '';
                    }}
                    value={selectedBrand}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'string') {
                        setSelectedBrand({ brandName: newValue });
                      } else if (newValue && newValue.inputValue) {
                        setSelectedBrand({ brandName: newValue.inputValue });
                      } else {
                        setSelectedBrand(newValue);
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);

                      const { inputValue } = params;
                      const isExisting = options.some((option) => inputValue === option.brandName);
                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          brandName: `Add "${inputValue}"`,
                        });
                      }

                      return filtered;
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    freeSolo
                    size="small"
                    renderOption={(props, option) => <li {...props}>{option.brandName}</li>}
                    renderInput={(params) => <TextField {...params}  placeholder="Select Existing or Add New Brand"/>}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 ,mt:3}}>
                  <Button className="action-btn cancel-btn" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                  <Button className="action-btn process-btn" variant="contained" onClick={saveBrand} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Mapping'}
                  </Button>
                </Box>
              </Box>

              <Paper sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderRadius: 2, overflow: 'hidden' }}>
                <GlobalMaterialTable
                  tableTitle="Selected NDCs"
                  tableCount={sessionData?.selected?.length || 0}
                  columns={[
                    { header: 'NDC', id: 'ndc', accessorFn: (row) => row?.request?.ndc || '-' },
                    { header: 'Source', id: 'source', accessorFn: (row) => row?.request?.source || '-' },
                    { header: groupHeader, id: 'group', accessorFn: (row) => getGroupLabel(row) },
                    { header: 'Brand', id: 'brand', accessorFn: (row) => getBrandLabel(row) },
                    { header: 'Date', id: 'date', accessorFn: (row) => row.lastUpdatedTime || '-' },
                    { header: 'Status', id: 'status', accessorFn: (row) => row.status || '-' },
                  ]}
                  data={sessionData?.selected || []}
                  emptyMessage="No selected NDCs found"
                  enablePagination
                  enableBottomToolbar
                  manualPagination={false}
                  page={pagination.pageIndex + 1}
                  pageSize={pagination.pageSize}
                  onPageChange={(p) => setPagination(prev => ({ ...prev, pageIndex: p - 1 }))}
                  onPageSizeChange={(ps) => setPagination(prev => ({ ...prev, pageSize: ps }))}
                  muiTablePaperProps={{ 
                    elevation: 0, 
                    sx: { 
                      border: 'none !important',
                      boxShadow: 'none !important' 
                    } 
                  }}
                />
              </Paper>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default DrugBrandMap;
